import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { message } from 'antd';

interface StudentData {
  MaHS: string;
  HoTen: string;
  NgaySinh: string;
  DiemTX?: number | null;
  DiemDK?: number | null;
  DiemThiL1?: number | null;
  DiemThiL2?: number | null;
  DiemTB_HeSo?: number;
  DiemTB_Mon?: number;
  DiemChu?: string;
  DiemThang4?: number;
  GhiChu?: string;
}

interface FilterInfo {
  TenLopHP: string;
  TenMonHoc: string;
  TenGV: string;
}

interface ExportData {
  filterInfo: FilterInfo;
  students: StudentData[];
}

/**
 * Xuất template để nhập điểm
 */
export const exportNhapDiemTemplate = ({ filterInfo, students }: ExportData) => {
  try {
    // Tạo worksheet trống
    const worksheet = XLSX.utils.aoa_to_sheet([]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Nhập điểm');

    // Thêm thông tin lớp học phần vào đầu file
    const headerInfo = [
      ['Lớp học phần:', filterInfo.TenLopHP],
      ['Môn học:', filterInfo.TenMonHoc],
      ['Giáo viên:', filterInfo.TenGV],
      [''] // Dòng trống
    ];
    XLSX.utils.sheet_add_aoa(worksheet, headerInfo, { origin: 'A1' });

    // Thêm header cột ở dòng 5
    XLSX.utils.sheet_add_aoa(worksheet, [['Mã HS', 'Họ tên', 'Ngày sinh', 'Điểm TX', 'Điểm ĐK']], { origin: 'A5' });

    // Thêm dữ liệu sinh viên bắt đầu từ dòng 6
    const studentData = students.map(student => [
      student.MaHS,
      student.HoTen,
      student.NgaySinh,
      student.DiemTX || '',
      student.DiemDK || ''
    ]);
    XLSX.utils.sheet_add_aoa(worksheet, studentData, { origin: 'A6' });

    const fileName = `Template_NhapDiem_${filterInfo.TenLopHP}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    message.success('Xuất template thành công!');
  } catch (error) {
    console.error('Error exporting template:', error);
    message.error('Lỗi khi xuất template');
  }
};

/**
 * Xuất bảng điểm hoàn chỉnh
 */
export const exportBangDiem = ({ filterInfo, students }: ExportData) => {
  try {
    // Tạo worksheet trống
    const worksheet = XLSX.utils.aoa_to_sheet([]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Bảng điểm');

    // Thêm thông tin lớp học phần vào đầu file
    const headerInfo = [
      ['Lớp học phần:', filterInfo.TenLopHP],
      ['Môn học:', filterInfo.TenMonHoc],
      ['Giáo viên:', filterInfo.TenGV],
      ['Ngày xuất:', new Date().toLocaleDateString('vi-VN')],
      [''] // Dòng trống
    ];
    XLSX.utils.sheet_add_aoa(worksheet, headerInfo, { origin: 'A1' });

    // Thêm header cột ở dòng 6
    const headers = [
      'Mã HS', 'Họ tên', 'Ngày sinh', 'Điểm TX', 'Điểm ĐK',
      'Điểm thi L1', 'Điểm thi L2', 'TB hệ số', 'TB môn',
      'Điểm chữ', 'Thang 4', 'Ghi chú'
    ];
    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: 'A6' });

    // Thêm dữ liệu sinh viên bắt đầu từ dòng 7
    const studentData = students.map(student => [
      student.MaHS,
      student.HoTen,
      student.NgaySinh,
      student.DiemTX || '',
      student.DiemDK || '',
      student.DiemThiL1 || '',
      student.DiemThiL2 || '',
      student.DiemTB_HeSo || '',
      student.DiemTB_Mon || '',
      student.DiemChu || '',
      student.DiemThang4 || '',
      student.GhiChu || ''
    ]);
    XLSX.utils.sheet_add_aoa(worksheet, studentData, { origin: 'A7' });

    const fileName = `BangDiem_${filterInfo.TenLopHP}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    message.success('Xuất bảng điểm thành công!');
  } catch (error) {
    console.error('Error exporting grades:', error);
    message.error('Lỗi khi xuất bảng điểm');
  }
};

/**
 * Import điểm từ file Excel
 */
export const importNhapDiemFromExcel = (file: File, callback: (data: any[]) => void) => {
  const reader = new FileReader();

  reader.onload = (e) => {
    try {
      const data = e.target?.result;
      const workbook = XLSX.read(data, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      // Chuyển đổi sheet thành JSON, bỏ qua các dòng header
      const jsonData = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        range: 4 // Bắt đầu từ dòng 5 (index 4) chứa header
      });

      // Lọc bỏ dòng header và các dòng trống
      const filteredData = jsonData.slice(1).filter((row: any) => row[0]); // Có Mã HS

      const importedList = filteredData.map((row: any) => ({
        MaHS: row[0]?.toString().trim(),
        HoTen: row[1]?.toString().trim(),
        NgaySinh: row[2]?.toString().trim(),
        DiemTX: row[3] !== undefined && row[3] !== '' ? parseFloat(row[3]) : null,
        DiemDK: row[4] !== undefined && row[4] !== '' ? parseFloat(row[4]) : null
      }));

      callback(importedList);
    } catch (error) {
      console.error('Error importing Excel:', error);
      message.error('Lỗi khi đọc file Excel');
    }
  };

  reader.readAsBinaryString(file);
};

/**
 * Validate dữ liệu điểm import
 */
export const validateImportedScores = (importedList: any[]): string[] => {
  const errors: string[] = [];

  importedList.forEach((item, index) => {
    const rowNum = index + 2; // +2 vì bắt đầu từ dòng 2 trong Excel

    if (!item.MaHS) {
      errors.push(`Dòng ${rowNum}: Thiếu mã học sinh`);
    }

    if (item.DiemTX !== null && (item.DiemTX < 0 || item.DiemTX > 10)) {
      errors.push(`Dòng ${rowNum}: Điểm TX phải từ 0 đến 10`);
    }

    if (item.DiemDK !== null && (item.DiemDK < 0 || item.DiemDK > 10)) {
      errors.push(`Dòng ${rowNum}: Điểm ĐK phải từ 0 đến 10`);
    }
  });

  return errors;
};
